import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { loadProducts } from '../redux/reducer';
import { RawProduct } from '../types';
import { AppDispatch } from '../redux/store';

export const useProducts = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { products, loading, error, productsLoaded } = useSelector(
    (state: RootState) => state.app
  );

  // Ensure products is always an array
  const safeProducts = products || [];

  // Load products when the hook is first used
  useEffect(() => {
    if (!productsLoaded && !loading) {
      dispatch(loadProducts());
    }
  }, [dispatch, productsLoaded, loading]);

  // Helper functions for filtering and searching
  const getProductsByCategory = (category: string): RawProduct[] => {
    return safeProducts.filter((product: RawProduct) => product.category === category);
  };

  const searchProducts = (searchTerm: string): RawProduct[] => {
    const term = searchTerm.toLowerCase();
    return safeProducts.filter((product: RawProduct) => 
      product.title.toLowerCase().includes(term) ||
      product.brand.toLowerCase().includes(term) ||
      product.shortDescription.toLowerCase().includes(term)
    );
  };

  const getProductById = (id: string): RawProduct | undefined => {
    return safeProducts.find((product: RawProduct) => product.unique_id === id);
  };

  const getCategories = (): string[] => {
    const categories = new Set(
      safeProducts
        .map((product: RawProduct) => product.category)
        .filter((category: string) => category && category.trim() !== '')
    );
    return Array.from(categories).sort() as string[];
  };

  const getSubCategories = (category: string): string[] => {
    const subCategories = new Set(
      safeProducts
        .filter((product: RawProduct) => product.category === category)
        .map((product: RawProduct) => product.sub_category1)
        .filter((subCategory: string) => subCategory && subCategory.trim() !== '')
    );
    return Array.from(subCategories).sort() as string[];
  };

  return {
    products: safeProducts,
    loading,
    error,
    productsLoaded,
    getProductsByCategory,
    searchProducts,
    getProductById,
    getCategories,
    getSubCategories,
  };
};
